import { observer } from 'mobx-react-lite'
import React, { useEffect, useState } from 'react'
import { Dimensions, Platform, StyleSheet, View } from 'react-native'

import { Bet, BetKind, BettingValidator } from '@ppwr/poker'

import { DS } from '../../../../../design-system'
import { translate } from '../../../../../i18n'
import { useStore } from '../../../../../models'
import { logger } from '../../../../../utils/logger'
import { BetModalActions } from './BetModalActions'
import { Info, Tabs } from './BetModalContent'
import { BetModalTop } from './BetModalTop'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { BottomSheetView } from '@gorhom/bottom-sheet'
import { triggerHaptic } from '../../../../../screens/new-design/utils/triggerHaptics/triggerHaptics'

export interface BetProps {
  onClose: () => void
}

const isWeb = Platform.OS === 'web'

export const BetInput = observer(({ onClose }: BetProps) => {
  const [amount, setAmount] = useState('')
  const [error, setError] = useState('')
  const betAmount = parseInt(amount)
  const insets = useSafeAreaInsets()

  const {
    game: {
      boardState,
      playersOnlyCalledBB,
      biggestBet,
      callAmount,
      currentBlind,
      doAllInForPlayer,
      doCallForPlayer,
      doBetForPlayer,
      myPlayer,
      minimumRaise,
      isGamePaused
    },
    permissions: { isSoundEnabled }
  } = useStore()

  const handleBet = async () => {
    if (isSoundEnabled) {
      triggerHaptic()
    }
    if (!myPlayer) {
      setError('Something went wrong!')
      return
    }

    if (!biggestBet && biggestBet !== 0) {
      setError('Something went wrong!')
      return
    }

    if (!amount) {
      setError('Your bet must be bigger than 0')
      return
    }

    if (betAmount === myPlayer.chips) {
      onClose()
      await doAllInForPlayer()
      return
    }

    if (callAmount === betAmount) {
      onClose()
      await doCallForPlayer()
      return
    }

    if ((!minimumRaise && minimumRaise !== 0) || playersOnlyCalledBB === undefined) {
      logger.error('Minimum Raise or playersOnlyCalledBB missing')
      return
    }

    /**
     * Edge case: When going through first betting round, if every player just calls BB, but BB wants to raise,
     * BB player would just be raising their BB bet. So in that case, assume player has not bet chips yet.
     */
    const isPlayerBB = myPlayer.role === 'BB'
    const isDeal = boardState === 'deal'
    // TODO: Fix Bet Class to expose the properties correctly to class transformer.
    // @ts-ignore
    const currentPlayerOnlyPlacedBB = myPlayer.bets.filter(Boolean).every((b) => b._kind === BetKind.BIGBLIND)

    let kind: BetKind
    if (biggestBet > 0) {
      kind = BetKind.RAISE
    } else {
      kind = BetKind.BET
    }

    const bet = new Bet(betAmount, kind)

    const isBBRaisingThemselves =
      isPlayerBB && isDeal && bet.kind === BetKind.RAISE && playersOnlyCalledBB && currentPlayerOnlyPlacedBB

    const playerAlreadyBetAmount = isBBRaisingThemselves ? 0 : myPlayer.bet
    const isValidBet = BettingValidator.isValidBetForPlayer(
      {
        bigBlind: currentBlind.big,
        biggestBet: biggestBet ?? Number.POSITIVE_INFINITY,
        playerAlreadyBet: playerAlreadyBetAmount,
        playerChips: myPlayer.chips,
        // If not bb is raising themselves edge case, substract already bet amount.
        // Just for validation, actual amount is handled in api.
        betAmount: bet.amount - (isBBRaisingThemselves ? 0 : myPlayer.bet),
        callAmount,
        minimumRaise
      },
      bet.kind
    )

    if (!isValidBet.ok) {
      setError(
        translate(`newGameScreen.betModalError.${isValidBet.error}`, {
          betAmount,
          bigBlind: currentBlind.big,
          biggestBet,
          minimumRaise,
          playerBet: myPlayer.bet,
          // TODO: Fix Bet Class
          // @ts-ignore
          kind: bet._kind.toLowerCase()
        })
      )
      setAmount('0')
      return
    }
    onClose()
    await doBetForPlayer(bet.amount, bet.kind)
  }

  useEffect(() => {
    if (betAmount > 0) {
      setError('')
    }
  }, [amount, betAmount])

  useEffect(() => {
    if (isGamePaused) {
      onClose()
    }
  }, [isGamePaused, onClose])

  return (
    <BottomSheetView style={Platform.OS !== 'web' ? { flex: 1 } : {}}>
      <DS.Frame style={[styles.container, isWeb ? { gap: 16 } : {}]}>
        <View style={styles.content}>
          <BetModalTop error={error} />
          <Info />
          <Tabs onSelect={setAmount} errorMsg={error} />
        </View>
        <DS.Frame style={[styles.buttonActionsContainer]}>
          <BetModalActions onClose={onClose} onConfirm={handleBet} isConfirmDisabled={!betAmount || betAmount <= 0} />
        </DS.Frame>
        <DS.Frame style={{ height: insets.bottom + 24 }} />
      </DS.Frame>
    </BottomSheetView>
  )
})

export const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingTop: 8,
    paddingHorizontal: 16,
    height: '100%'
  },
  content: {
    gap: 8,
    flexGrow: 1
  },
  buttonActionsContainer: {
    alignSelf: 'flex-end',
    alignContent: 'flex-end',
    width: '100%',
    paddingHorizontal: 16
  }
})
